/**
 * Geometry for the facial treatment map.
 *
 * Kept separate from the component for the same reason the tooth chart's is:
 * anatomy is data, and data can be asserted on. A region in the wrong place is
 * a clinical error that no amount of component testing catches if the shapes
 * live inline in JSX.
 *
 * Coordinates are in a 200×260 viewBox — a front-facing face. As with the tooth
 * chart, the VIEWER'S left is the PATIENT'S right, because that is how a
 * clinician sees someone in the chair. The map is never mirrored under RTL:
 * anatomy is not text.
 */

export const FACE_W = 200;
export const FACE_H = 260;

/**
 * The face outline and features, drawn behind the regions purely so the map
 * reads as a face. Nothing here is clickable.
 */
export const FACE_OUTLINE = {
  head: 'M100,8 C147,8 172,44 172,96 C172,150 150,206 100,246 C50,206 28,150 28,96 C28,44 53,8 100,8 Z',
  eyeL: { cx: 71, cy: 96, rx: 12, ry: 6 },
  eyeR: { cx: 129, cy: 96, rx: 12, ry: 6 },
  nose: 'M100,104 L100,134 M100,134 C94,138 90,140 86,140 M100,134 C106,138 110,140 114,140',
  mouth: 'M78,172 C88,166 112,166 122,172 C112,180 88,180 78,172 Z',
};

/**
 * Hit regions, keyed exactly as the server's `face-map.core.ts`. A key here
 * with no counterpart there renders nothing the record can store, so the two
 * lists are checked against each other in the tests.
 *
 * `shape` is either a circle (a point-like target) or a path (an area).
 */
export const REGION_SHAPES = {
  // ── Upper face ──────────────────────────────────────────────────────────
  forehead: { kind: 'path', d: 'M52,42 C70,28 130,28 148,42 C148,62 142,70 100,70 C58,70 52,62 52,42 Z' },
  glabella: { kind: 'circle', cx: 100, cy: 80, r: 9 },
  brow_l: { kind: 'path', d: 'M56,80 C62,72 80,72 86,78 L86,86 C78,80 64,80 56,88 Z' },
  brow_r: { kind: 'path', d: 'M144,80 C138,72 120,72 114,78 L114,86 C122,80 136,80 144,88 Z' },
  crows_feet_l: { kind: 'circle', cx: 50, cy: 100, r: 10 },
  crows_feet_r: { kind: 'circle', cx: 150, cy: 100, r: 10 },

  // ── Mid face ────────────────────────────────────────────────────────────
  tear_trough_l: { kind: 'path', d: 'M59,106 C66,112 78,112 84,108 L84,116 C76,120 64,118 59,114 Z' },
  tear_trough_r: { kind: 'path', d: 'M141,106 C134,112 122,112 116,108 L116,116 C124,120 136,118 141,114 Z' },
  cheek_l: { kind: 'circle', cx: 60, cy: 132, r: 16 },
  cheek_r: { kind: 'circle', cx: 140, cy: 132, r: 16 },
  nose: { kind: 'path', d: 'M92,106 L108,106 L112,142 C106,148 94,148 88,142 Z' },
  nasolabial_l: { kind: 'path', d: 'M84,148 C78,158 76,168 78,178 L86,176 C84,166 86,158 91,150 Z' },
  nasolabial_r: { kind: 'path', d: 'M116,148 C122,158 124,168 122,178 L114,176 C116,166 114,158 109,150 Z' },

  // ── Lower face ──────────────────────────────────────────────────────────
  lips_upper: { kind: 'path', d: 'M78,172 C88,164 112,164 122,172 C112,170 88,170 78,172 Z' },
  lips_lower: { kind: 'path', d: 'M78,174 C88,184 112,184 122,174 C112,178 88,178 78,174 Z' },
  marionette_l: { kind: 'path', d: 'M76,182 C74,192 74,200 78,208 L86,204 C82,197 82,190 84,184 Z' },
  marionette_r: { kind: 'path', d: 'M124,182 C126,192 126,200 122,208 L114,204 C118,197 118,190 116,184 Z' },
  chin: { kind: 'circle', cx: 100, cy: 208, r: 13 },
  jawline_l: { kind: 'path', d: 'M46,158 C48,186 62,212 84,228 L80,236 C54,220 38,190 36,160 Z' },
  jawline_r: { kind: 'path', d: 'M154,158 C152,186 138,212 116,228 L120,236 C146,220 162,190 164,160 Z' },
  masseter_l: { kind: 'circle', cx: 50, cy: 150, r: 11 },
  masseter_r: { kind: 'circle', cx: 150, cy: 150, r: 11 },
  neck: { kind: 'path', d: 'M74,244 L126,244 L126,258 L74,258 Z' },
};

/** Which treatments each region takes — mirrors the server's catalogue. */
export const REGION_TREATMENTS = {
  forehead: ['TOXIN', 'ENERGY'],
  glabella: ['TOXIN', 'FILLER'],
  brow_l: ['TOXIN', 'THREAD'],
  brow_r: ['TOXIN', 'THREAD'],
  crows_feet_l: ['TOXIN', 'ENERGY'],
  crows_feet_r: ['TOXIN', 'ENERGY'],
  tear_trough_l: ['FILLER'],
  tear_trough_r: ['FILLER'],
  cheek_l: ['FILLER', 'THREAD', 'ENERGY'],
  cheek_r: ['FILLER', 'THREAD', 'ENERGY'],
  nose: ['FILLER'],
  nasolabial_l: ['FILLER'],
  nasolabial_r: ['FILLER'],
  lips_upper: ['FILLER', 'TOXIN'],
  lips_lower: ['FILLER'],
  marionette_l: ['FILLER'],
  marionette_r: ['FILLER'],
  chin: ['FILLER', 'TOXIN'],
  jawline_l: ['FILLER', 'THREAD'],
  jawline_r: ['FILLER', 'THREAD'],
  masseter_l: ['TOXIN'],
  masseter_r: ['TOXIN'],
  neck: ['TOXIN', 'ENERGY'],
};

/** English fallback names. Display names come from the pack's translations. */
export const REGION_NAMES = {
  forehead: 'Forehead',
  glabella: 'Glabella',
  brow_l: 'Brow (patient right)',
  brow_r: 'Brow (patient left)',
  crows_feet_l: "Crow's feet (patient right)",
  crows_feet_r: "Crow's feet (patient left)",
  tear_trough_l: 'Tear trough (patient right)',
  tear_trough_r: 'Tear trough (patient left)',
  cheek_l: 'Cheek (patient right)',
  cheek_r: 'Cheek (patient left)',
  nose: 'Nose',
  nasolabial_l: 'Nasolabial fold (patient right)',
  nasolabial_r: 'Nasolabial fold (patient left)',
  lips_upper: 'Upper lip',
  lips_lower: 'Lower lip',
  marionette_l: 'Marionette line (patient right)',
  marionette_r: 'Marionette line (patient left)',
  chin: 'Chin',
  jawline_l: 'Jawline (patient right)',
  jawline_r: 'Jawline (patient left)',
  masseter_l: 'Masseter (patient right)',
  masseter_r: 'Masseter (patient left)',
  neck: 'Neck',
};

export const REGION_KEYS = Object.keys(REGION_SHAPES);

/**
 * The mirrored region, or undefined for a midline one.
 *
 * Aesthetic treatment is usually symmetric, so "do the other side too" is a
 * first-class action rather than a convenience.
 */
export function mirrorOf(key) {
  if (key.endsWith('_l')) {
    const other = `${key.slice(0, -2)}_r`;
    return REGION_SHAPES[other] ? other : undefined;
  }
  if (key.endsWith('_r')) {
    const other = `${key.slice(0, -2)}_l`;
    return REGION_SHAPES[other] ? other : undefined;
  }
  return undefined;
}

/** Sums units per treatment — the same computation the server exposes. */
export function totalsByTreatment(members) {
  const totals = {};
  for (const m of members ?? []) {
    if (m?.units === undefined || m?.units === null || !m?.treatment) continue;
    const n = Number(m.units);
    if (!Number.isFinite(n)) continue;
    totals[m.treatment] = (totals[m.treatment] ?? 0) + n;
  }
  return totals;
}
